// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/assessment/ProductiveItems.jsx
//  ENGLISH ASSESSMENT v2 — Writing y Speaking
//
//  Mismo contrato que el resto de reactivos: onSubmit entrega la respuesta
//  cruda. Aquí no hay clave que comparar — el servidor la manda a evaluar.
//
//  ── PRINCIPIO DE DISEÑO: LA TAREA DEBE VERSE CORTA ──────────────────────────
//  Un cuadro de texto grande y vacío hace que la gente abandone. Por eso:
//    · El rango de palabras se dice ANTES de escribir, y es bajo (15-70).
//    · El contador va en vivo y se pone verde en cuanto alcanza el mínimo,
//      para que el alumno vea que ya terminó en vez de sentir que le falta.
//    · Pasarse del máximo NO bloquea: el rango orienta, no castiga.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
import { LIMITES } from '../../limites';
import { createRecognition, isRecognitionAvailable } from '../../utils/browserSpeech';

/** Mismo conteo que aplica el servidor, para que el contador no mienta. */
function countWords(text) {
  if (!text) return 0;
  return (text.match(/[A-Za-z0-9'’-]+/g) || []).filter((w) => w.replace(/['-]/g, '')).length;
}

function TaskCard({ payload }) {
  return (
    <div className="as-task">
      <p className="as-task-prompt">{payload.prompt}</p>
      {payload.context && <p className="as-task-context">{payload.context}</p>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   WRITING
   ───────────────────────────────────────────────────────────────────────────── */

export function WritingItem({ item, onSubmit, disabled }) {
  const { payload } = item;
  const [text, setText] = useState('');

  useEffect(() => setText(''), [item.item_id]);

  const words = countWords(text);
  const min = payload.min_words || 0;
  const max = payload.max_words || 0;
  const enough = words >= min;

  return (
    <>
      <TaskCard payload={payload} />

      <div className="as-writebox">
        <textarea
            maxLength={LIMITES.respuestaEjercicio}
          className="as-textarea"
          placeholder="Escribe tu respuesta en inglés…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={disabled}
          rows={6}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
        />

        <div className="as-counter">
          <span className={enough ? 'as-count-ok' : 'as-count-low'}>
            {words} {words === 1 ? 'palabra' : 'palabras'}
          </span>
          <span className="as-counter-goal">
            {enough ? '✓ ya alcanzaste el mínimo' : `objetivo: ${min}-${max}`}
          </span>
        </div>
      </div>

      <button
        type="button"
        className="as-btn"
        disabled={!enough || disabled}
        onClick={() => onSubmit({ text: text.trim() })}
      >
        {enough ? 'Enviar respuesta' : `Escribe al menos ${min} palabras`}
      </button>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SPEAKING
   ───────────────────────────────────────────────────────────────────────────── */

export function SpeakingItem({ item, onSubmit, disabled }) {
  const { payload } = item;
  const limit = payload.seconds || 45;

  const [phase, setPhase] = useState('idle');   // idle | recording | done
  const [transcript, setTranscript] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);

  const recRef = useRef(null);
  const startedAt = useRef(0);
  const timerRef = useRef(null);

  const supported = isRecognitionAvailable();

  const finish = useCallback(() => {
    clearInterval(timerRef.current);
    recRef.current?.stop();
    setPhase('done');
  }, []);

  useEffect(() => {
    // Reinicio total al cambiar de tarea.
    setPhase('idle'); setTranscript(''); setElapsed(0); setError(null);
    return () => {
      clearInterval(timerRef.current);
      recRef.current?.stop();
    };
  }, [item.item_id]);

  const start = () => {
    if (disabled || phase !== 'idle') return;

    const rec = createRecognition({
      onPartial: setTranscript,
      onFinal: (t) => setTranscript((prev) => t || prev),
      onError: (code) => {
        setError(
          code === 'not-allowed'
            ? 'No pudimos usar tu micrófono. Revisa los permisos del navegador.'
            : 'El reconocimiento de voz falló. Puedes intentar de nuevo.'
        );
        finish();
      },
    });
    if (!rec) return;

    recRef.current = rec;
    startedAt.current = Date.now();
    setPhase('recording');
    rec.start();

    timerRef.current = setInterval(() => {
      const secs = (Date.now() - startedAt.current) / 1000;
      setElapsed(secs);
      if (secs >= limit) finish();
    }, 200);
  };

  const words = countWords(transcript);
  const min = payload.min_words || 0;
  const enough = words >= min;
  const duration = elapsed || 0;
  const pct = Math.min(100, (duration / limit) * 100);

  /* Navegador sin reconocimiento de voz: se ofrece escribir la respuesta.
     Es una medición distinta y peor, pero deja al alumno terminar el examen —
     y queda registrado como transcripción tecleada. */
  if (!supported) {
    return (
      <>
        <TaskCard payload={payload} />
        <p className="as-hint">
          Tu navegador no puede grabar voz. Para no dejarte a medias, escribe lo
          que dirías — esta respuesta se evaluará como producción escrita.
        </p>
        <WritingItem item={item} onSubmit={onSubmit} disabled={disabled} />
      </>
    );
  }

  return (
    <>
      <TaskCard payload={payload} />

      <div className="as-audio">
        {phase === 'idle' && (
          <>
            <button type="button" className="as-play as-mic" onClick={start} disabled={disabled}>
              <span aria-hidden="true">🎙️</span>
            </button>
            <p className="as-hint">Toca y habla. Máximo {limit} segundos.</p>
          </>
        )}

        {phase === 'recording' && (
          <>
            <button type="button" className="as-play as-mic as-playing" onClick={finish}>
              <span className="as-wave" aria-hidden="true" />
              <span className="as-wave" aria-hidden="true" />
              <span aria-hidden="true">⏹</span>
            </button>
            <div className="as-timer">
              <div className="as-timer-fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="as-hint">
              {Math.floor(duration)}s / {limit}s · toca para terminar
            </p>
          </>
        )}

        {phase === 'done' && (
          <p className="as-hint">
            {Math.floor(duration)} segundos · {words} {words === 1 ? 'palabra' : 'palabras'}
          </p>
        )}
      </div>

      {/* Transcripción en vivo: el alumno ve que se le está entendiendo.
          Es también honestidad — sabe exactamente qué se va a evaluar. */}
      {(phase === 'recording' || phase === 'done') && (
        <div className="as-transcript">
          <span className="as-transcript-label">Lo que escuchamos</span>
          <p>{transcript || <em>…</em>}</p>
        </div>
      )}

      {error && <p className="as-hint as-center">{error}</p>}

      {phase === 'done' && (
        <button
          type="button"
          className="as-btn"
          disabled={disabled}
          onClick={() => onSubmit({ text: transcript.trim(), duration_seconds: duration })}
        >
          {enough ? 'Enviar respuesta' : 'Enviar así (quedó corta)'}
        </button>
      )}
    </>
  );
}
